import type { ContentType } from "@brookbush/database";
import type { Comment } from "@brookbush/types";

import { StarFilledIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { formatDistance } from "date-fns";
import { ReplyIcon } from "lucide-react";
import Image from "next/image";
import { memo, useMemo, useState } from "react";

import CommentActions from "./CommentActions";
import CommentEditor from "./CommentEditor";
import CommentWrapper from "./CommentWrapper";
import ConfirmationModal from "./ConfirmationModal";
import { getCommentRepliesRecursive } from "~/lib/comments.lib";

interface CommentProps {
	comment: Comment;
	contentId: string;
	contentType: ContentType;
	addComment: (body: string, parentComment?: Comment) => Promise<void>;
	updateComment: (id: number, body: string) => Promise<void>;
	removeComment: (id: number) => Promise<void>;
}

const Comment = ({
	comment,

	...props
}: CommentProps) => {
	const { addComment, updateComment, removeComment } = props;
	const [deletingComment, setDeletingComment] = useState<Comment | null>(null);

	const [editingComment, setEditingComment] = useState<Comment | null>(null);

	const [replyingComment, setReplyingComment] = useState<Comment | null>(null);

	const recursiveReplies = useMemo(
		() => getCommentRepliesRecursive(comment),
		[comment],
	);

	const replyImages = useMemo(
		() =>
			Array.from(
				new Set(recursiveReplies.map((comment) => comment.user.imageUrl)),
			),
		[recursiveReplies],
	);

	const [repliesVisible, setRepliesVisible] = useState<boolean>(false);

	if (
		!comment.replies?.filter((reply) => !reply.isDeleted).length &&
		comment.isDeleted
	)
		return null;

	const isSiteAdmin = comment.user.primaryEmailAddress.endsWith(
		"@brookbushinstitute.com",
	);

	return (
		<CommentWrapper comment={comment}>
			<ConfirmationModal
				title="Confirmation"
				description="Are you sure you want to delete this comment?"
				open={!!deletingComment}
				onConfirm={() => {
					removeComment(deletingComment!.id);
					setDeletingComment(null);
				}}
				onClose={() => setDeletingComment(null)}
			/>

			<div className="flex flex-col gap-6" id={`comment-${comment.id}`}>
				{editingComment === comment ? (
					<CommentEditor
						initialValue={comment.body}
						success={{
							label: "Update",
							onClick: async (value) => {
								await updateComment(comment.id, value);
								setEditingComment(null);
							},
						}}
						failure={{
							label: "Cancel",
							onClick: () => setEditingComment(null),
						}}
					/>
				) : (
					<div
						className={clsx(
							"relative flex w-full max-w-max flex-1 flex-col gap-1.5 phablet:gap-2 rounded-2xl rounded-tl-none border border-slate-200 phablet:px-4 px-3 phablet:pt-3 pt-2.5 pb-2 phablet:pb-2.5 shadow shadow-slate-100 dark:border-slate-600 dark:shadow-slate-900",
							{
								"opacity-75 shadow-none": comment.isDeleted,
							},
							{
								"dark:!border-amber-300/40 border-amber-600/20": isSiteAdmin,
							},
						)}
					>
						<div className="flex items-start justify-between gap-10">
							<div className="flex gap-2">
								<div className="relative h-6 phablet:h-7 phablet:w-7 w-6 overflow-hidden rounded-full border">
									<Image
										unoptimized
										src={
											comment.user.imageUrl ||
											"https://www.gravatar.com/avatar?d=mp"
										}
										alt={`${comment.user.firstName} ${comment.user.lastName}`}
										fill
									/>
								</div>

								<h3 className="flex items-center gap-1.5 font-medium text-sm xl:text-base">
									{comment.user.firstName || comment.user.username}{" "}
									{comment.user.lastName}{" "}
									{isSiteAdmin && (
										<StarFilledIcon
											className="text-amber-500"
											width={18}
											height={18}
										/>
									)}
								</h3>
							</div>

							<p className="hidden text-sm sm:block">
								{formatDistance(new Date(), new Date(comment.createdAt))} ago
							</p>
						</div>

						<div className="flex flex-col gap-1">
							{comment.isDeleted ? (
								<p className="w-full break-words bg-transparent xl:text-base dark:bg-transparent">
									This comment has been deleted
								</p>
							) : (
								<p className="w-full break-words bg-transparent xl:text-base dark:bg-transparent">
									{comment.body}
								</p>
							)}
						</div>

						{!comment.isDeleted && (
							<div className="-mt-1 mb-1 ml-auto">
								<CommentActions
									comment={comment}
									handleDelete={() => setDeletingComment(comment)}
									handleAddReply={() => setReplyingComment(comment)}
									handleEdit={() => setEditingComment(comment)}
								/>
							</div>
						)}
					</div>
				)}

				{!!recursiveReplies.length &&
					(!repliesVisible ? (
						<button
							onClick={() => setRepliesVisible(true)}
							className="ml-5 flex items-center gap-1 self-start rounded-full bg-slate-100 px-3.5 py-2 font-medium hover:brightness-95 dark:bg-slate-700 dark:hover:brightness-105"
						>
							<ReplyIcon className="rotate-180" size={20} />{" "}
							{recursiveReplies.length}{" "}
							{recursiveReplies.length === 1 ? "reply" : "replies"}
							<div className="ml-1 flex">
								{replyImages.map((imageUrl) => (
									<div
										key={imageUrl}
										className="relative -ml-1 h-6 w-6 overflow-hidden rounded-full border first:ml-0"
									>
										<Image
											src={imageUrl}
											alt=""
											className="object-cover"
											fill
										/>
									</div>
								))}
							</div>
						</button>
					) : (
						<div className="flex flex-col gap-6 border-l">
							{comment.replies?.map((reply) => (
								<Comment comment={reply} {...props} key={reply.id} />
							))}
						</div>
					))}

				{replyingComment === comment && (
					<div className="ml-8">
						<CommentEditor
							success={{
								label: "Reply",
								onClick: async (value) => {
									await addComment(value, comment);
									setReplyingComment(null);
								},
							}}
							failure={{
								label: "Cancel",
								onClick: () => setReplyingComment(null),
							}}
						/>
					</div>
				)}
			</div>
		</CommentWrapper>
	);
};

export default memo(Comment);
